import { useState, useEffect, useRef } from "react";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import { 
  Plus, 
  BookOpen, 
  Trash2, 
  Edit, 
  PlayCircle, 
  FileText,
  Calendar,
  Link as LinkIcon,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Save,
  X,
  Layers,
  Upload,
  File,
  Loader2,
  Download,
  FileUp
} from "lucide-react";

const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isMaterialOpen, setIsMaterialOpen] = useState(false);
  const [isAssignmentOpen, setIsAssignmentOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCurriculumOpen, setIsCurriculumOpen] = useState(false);
  
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    short_description: '',
    instructor: '',
    category: '',
    duration: '',
    thumbnail: '',
    price: 0,
    skill_level: 'Beginner',
    curriculum: []
  });

  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    short_description: '',
    instructor: '',
    category: '',
    duration: '',
    thumbnail: '',
    price: 0,
    skill_level: 'Beginner',
    curriculum: []
  });

  const [materialForm, setMaterialForm] = useState({
    course_id: '',
    title: '',
    type: 'video',
    url: '',
    description: ''
  });

  const [assignmentForm, setAssignmentForm] = useState({
    course_id: '',
    title: '',
    description: '',
    due_date: '',
    total_marks: 100
  });

  // Curriculum state
  const [curriculumModules, setCurriculumModules] = useState([]);
  const [curriculumImportFile, setCurriculumImportFile] = useState(null);
  const [importingCurriculum, setImportingCurriculum] = useState(false);
  const curriculumFileRef = useRef(null);
  
  // File upload state
  const [materialFile, setMaterialFile] = useState(null);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const materialFileRef = useRef(null);
  
  // Drag and drop state for lessons
  const [draggedLesson, setDraggedLesson] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, { withCredentials: true });
      setCourses(response.data);
    } catch (error) {
      toast.error("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetails = async (courseId) => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
      setSelectedCourse(response.data);
    } catch (error) {
      toast.error("Failed to load course details");
    }
  };

  const handleCreateCourse = async () => {
    if (!courseForm.title || !courseForm.description || !courseForm.instructor) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/courses`, courseForm, { withCredentials: true });
      toast.success("Course created successfully!");
      setIsCreateOpen(false);
      setCourseForm({ 
        title: '', description: '', short_description: '', instructor: '', 
        category: '', duration: '', thumbnail: '', price: 0, 
        skill_level: 'Beginner', curriculum: [] 
      });
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create course");
    }
  };

  const handleUpdateCourse = async () => {
    if (!editForm.title || !editForm.description || !editForm.instructor) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.put(`${API}/courses/${selectedCourse.course_id}`, editForm, { withCredentials: true });
      toast.success("Course updated successfully!");
      setIsEditOpen(false);
      fetchCourses();
      fetchCourseDetails(selectedCourse.course_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update course");
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course?")) return;

    try {
      await axios.delete(`${API}/courses/${courseId}`, { withCredentials: true });
      toast.success("Course deleted");
      fetchCourses();
      if (selectedCourse?.course_id === courseId) {
        setSelectedCourse(null);
      }
    } catch (error) {
      toast.error("Failed to delete course");
    }
  };

  const openEditDialog = () => {
    setEditForm({
      title: selectedCourse.title || '',
      description: selectedCourse.description || '',
      short_description: selectedCourse.short_description || '',
      instructor: selectedCourse.instructor || '',
      category: selectedCourse.category || '',
      duration: selectedCourse.duration || '',
      thumbnail: selectedCourse.thumbnail || '',
      price: selectedCourse.price || 0,
      skill_level: selectedCourse.skill_level || 'Beginner',
      curriculum: selectedCourse.curriculum || []
    });
    setIsEditOpen(true);
  };

  const openCurriculumEditor = () => {
    setCurriculumModules(selectedCourse.curriculum || []);
    setIsCurriculumOpen(true);
  };

  // Curriculum management functions
  const addModule = () => {
    setCurriculumModules([
      ...curriculumModules,
      {
        module_title: `Module ${curriculumModules.length + 1}`,
        module_description: '',
        lessons: []
      }
    ]);
  };

  const updateModule = (index, field, value) => {
    const updated = [...curriculumModules];
    updated[index][field] = value;
    setCurriculumModules(updated);
  };

  const deleteModule = (index) => {
    setCurriculumModules(curriculumModules.filter((_, i) => i !== index));
  };

  const moveModule = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= curriculumModules.length) return;
    
    const updated = [...curriculumModules];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setCurriculumModules(updated);
  };

  const addLesson = (moduleIndex) => {
    const updated = [...curriculumModules];
    updated[moduleIndex].lessons = [
      ...(updated[moduleIndex].lessons || []),
      {
        title: `Lesson ${(updated[moduleIndex].lessons?.length || 0) + 1}`,
        type: 'video',
        duration: '10 min'
      }
    ];
    setCurriculumModules(updated);
  };

  const updateLesson = (moduleIndex, lessonIndex, field, value) => {
    const updated = [...curriculumModules];
    updated[moduleIndex].lessons[lessonIndex][field] = value;
    setCurriculumModules(updated);
  };

  const deleteLesson = (moduleIndex, lessonIndex) => {
    const updated = [...curriculumModules];
    updated[moduleIndex].lessons = updated[moduleIndex].lessons.filter((_, i) => i !== lessonIndex);
    setCurriculumModules(updated);
  };

  // Drag and drop handlers for lessons
  const handleLessonDragStart = (moduleIndex, lessonIndex) => {
    setDraggedLesson({ moduleIndex, lessonIndex });
  };

  const handleLessonDragOver = (e, moduleIndex, lessonIndex) => {
    e.preventDefault();
    if (!draggedLesson) return;
    if (draggedLesson.moduleIndex === moduleIndex && draggedLesson.lessonIndex === lessonIndex) return;

    const updated = [...curriculumModules];
    const sourceModule = updated[draggedLesson.moduleIndex];
    const targetModule = updated[moduleIndex];
    
    // Remove from source
    const [draggedItem] = sourceModule.lessons.splice(draggedLesson.lessonIndex, 1);
    
    // Add to target
    targetModule.lessons.splice(lessonIndex, 0, draggedItem);
    
    setCurriculumModules(updated);
    setDraggedLesson({ moduleIndex, lessonIndex });
  };

  const handleLessonDragEnd = () => {
    setDraggedLesson(null);
  };

  // Move lesson within module
  const moveLessonInModule = (moduleIndex, lessonIndex, direction) => {
    const newIndex = direction === 'up' ? lessonIndex - 1 : lessonIndex + 1;
    const lessons = curriculumModules[moduleIndex].lessons;
    if (newIndex < 0 || newIndex >= lessons.length) return;
    
    const updated = [...curriculumModules];
    [updated[moduleIndex].lessons[lessonIndex], updated[moduleIndex].lessons[newIndex]] = 
      [updated[moduleIndex].lessons[newIndex], updated[moduleIndex].lessons[lessonIndex]];
    setCurriculumModules(updated);
  };

  const saveCurriculum = async () => {
    try {
      await axios.put(`${API}/courses/${selectedCourse.course_id}`, {
        ...selectedCourse,
        curriculum: curriculumModules
      }, { withCredentials: true });
      toast.success("Curriculum saved successfully!");
      setIsCurriculumOpen(false);
      fetchCourseDetails(selectedCourse.course_id);
    } catch (error) {
      toast.error("Failed to save curriculum");
    }
  };

  // Import curriculum from file
  const handleImportCurriculum = async (replaceExisting = false) => {
    if (!curriculumImportFile) {
      toast.error("Please select a file to import");
      return;
    }

    setImportingCurriculum(true);
    try {
      const formData = new FormData();
      formData.append('file', curriculumImportFile);
      formData.append('replace_existing', replaceExisting);

      const response = await axios.post(
        `${API}/courses/${selectedCourse.course_id}/curriculum/import-file`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success(response.data.message);
      setCurriculumImportFile(null);
      if (curriculumFileRef.current) {
        curriculumFileRef.current.value = '';
      }
      // Refresh course details to get updated curriculum
      const courseRes = await axios.get(`${API}/courses/${selectedCourse.course_id}`, { withCredentials: true });
      setCurriculumModules(courseRes.data.curriculum || []);
      fetchCourseDetails(selectedCourse.course_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import curriculum");
    } finally {
      setImportingCurriculum(false);
    }
  };

  const downloadCurriculumTemplate = () => {
    const template = "module_title,lesson_title,lesson_duration,lesson_type\nIntroduction,Welcome to the Course,5 min,video\nIntroduction,Course Overview,10 min,video\nCore Concepts,Understanding Basics,15 min,video\nCore Concepts,Practice Exercise,20 min,assignment\n";
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'curriculum_template.csv';
    a.click();
  };

  const handleAddMaterial = async () => {
    if (!materialForm.title || !selectedCourse) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // First create the material
      const materialRes = await axios.post(`${API}/materials`, {
        ...materialForm,
        course_id: selectedCourse.course_id
      }, { withCredentials: true });
      
      const materialId = materialRes.data.material_id;
      
      // If there's a file, upload it
      if (materialFile) {
        setUploadingMaterial(true);
        setUploadProgress(0);
        
        const formData = new FormData();
        formData.append('file', materialFile);
        
        await axios.post(`${API}/materials/${materialId}/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
        
        setUploadingMaterial(false);
      }
      
      toast.success("Material added!");
      setIsMaterialOpen(false);
      setMaterialForm({ course_id: '', title: '', type: 'video', url: '', description: '' });
      setMaterialFile(null);
      setUploadProgress(0);
      fetchCourseDetails(selectedCourse.course_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add material");
      setUploadingMaterial(false);
    }
  };

  const handleMaterialFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (500MB limit for materials)
      if (file.size > 500 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 500MB");
        return;
      }
      setMaterialFile(file);
    }
  };

  const removeMaterialFile = () => {
    setMaterialFile(null);
    if (materialFileRef.current) {
      materialFileRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleAddAssignment = async () => {
    if (!assignmentForm.title || !assignmentForm.due_date || !selectedCourse) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/assignments`, {
        ...assignmentForm,
        course_id: selectedCourse.course_id,
        due_date: new Date(assignmentForm.due_date).toISOString()
      }, { withCredentials: true });
      toast.success("Assignment created!");
      setIsAssignmentOpen(false);
      setAssignmentForm({ course_id: '', title: '', description: '', due_date: '', total_marks: 100 });
      fetchCourseDetails(selectedCourse.course_id);
    } catch (error) {
      toast.error("Failed to create assignment");
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    try {
      await axios.delete(`${API}/materials/${materialId}`, { withCredentials: true });
      toast.success("Material deleted");
      fetchCourseDetails(selectedCourse.course_id);
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API}/assignments/${assignmentId}`, { withCredentials: true });
      toast.success("Assignment deleted");
      fetchCourseDetails(selectedCourse.course_id);
    } catch (error) {
      toast.error("Failed to delete assignment");
    }
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <PlayCircle className="h-4 w-4 text-violet-600" />;
      case 'document': return <FileText className="h-4 w-4 text-blue-600" />;
      case 'assignment': return <BookOpen className="h-4 w-4 text-emerald-600" />;
      default: return <FileText className="h-4 w-4 text-stone-400" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-courses">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Manage Courses
            </h1>
            <p className="text-stone-600 mt-1">Create and manage course content</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-700 hover:bg-violet-800 rounded-full" data-testid="create-course-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Course
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Create New Course</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    placeholder="Course title"
                    data-testid="course-title-input"
                  />
                </div>
                <div>
                  <Label>Short Description</Label>
                  <Input
                    value={courseForm.short_description}
                    onChange={(e) => setCourseForm({ ...courseForm, short_description: e.target.value })}
                    placeholder="Brief course summary (shown on cards)"
                  />
                </div>
                <div>
                  <Label>Full Description *</Label>
                  <Textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    placeholder="Detailed course description"
                    rows={3}
                    data-testid="course-description-input"
                  />
                </div>
                <div>
                  <Label>Instructor *</Label>
                  <Input
                    value={courseForm.instructor}
                    onChange={(e) => setCourseForm({ ...courseForm, instructor: e.target.value })}
                    placeholder="Instructor name"
                    data-testid="course-instructor-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Input
                      value={courseForm.category}
                      onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                      placeholder="e.g., Programming"
                      data-testid="course-category-input"
                    />
                  </div>
                  <div>
                    <Label>Duration</Label>
                    <Input
                      value={courseForm.duration}
                      onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                      placeholder="e.g., 10 hours"
                      data-testid="course-duration-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Price (GBP) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={courseForm.price}
                      onChange={(e) => setCourseForm({ ...courseForm, price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      data-testid="course-price-input"
                    />
                  </div>
                  <div>
                    <Label>Skill Level</Label>
                    <Select 
                      value={courseForm.skill_level} 
                      onValueChange={(v) => setCourseForm({ ...courseForm, skill_level: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Beginner">Beginner</SelectItem>
                        <SelectItem value="Intermediate">Intermediate</SelectItem>
                        <SelectItem value="Advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Thumbnail URL</Label>
                  <Input
                    value={courseForm.thumbnail}
                    onChange={(e) => setCourseForm({ ...courseForm, thumbnail: e.target.value })}
                    placeholder="https://..."
                    data-testid="course-thumbnail-input"
                  />
                </div>
                <Button 
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  onClick={handleCreateCourse}
                  data-testid="submit-course-btn"
                >
                  Create Course
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Course List & Details */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Course List */}
          <div className="space-y-4">
            <h2 className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit' }}>Courses ({courses.length})</h2>
            {courses.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardContent className="p-8 text-center">
                  <BookOpen className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">No courses yet</p>
                </CardContent>
              </Card>
            ) : (
              courses.map((course) => (
                <Card 
                  key={course.course_id}
                  className={`bg-white rounded-xl border cursor-pointer transition-all ${
                    selectedCourse?.course_id === course.course_id
                      ? 'ring-2 ring-violet-700 border-violet-200'
                      : 'border-stone-100 hover:shadow-md'
                  }`}
                  onClick={() => fetchCourseDetails(course.course_id)}
                  data-testid={`course-item-${course.course_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-stone-900 truncate">{course.title}</h3>
                        <p className="text-sm text-stone-500 truncate">{course.instructor}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="secondary" className="text-xs">
                            {course.category || 'General'}
                          </Badge>
                          {course.price > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                              £{course.price}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCourse(course.course_id);
                        }}
                        data-testid={`delete-course-${course.course_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Course Details */}
          <div className="lg:col-span-2">
            {selectedCourse ? (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader className="border-b border-stone-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle style={{ fontFamily: 'Outfit' }}>{selectedCourse.title}</CardTitle>
                      <p className="text-stone-500 text-sm mt-1">{selectedCourse.instructor}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline">{selectedCourse.skill_level || 'Beginner'}</Badge>
                        {selectedCourse.duration && <Badge variant="secondary">{selectedCourse.duration}</Badge>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={openEditDialog} data-testid="edit-course-btn">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={openCurriculumEditor}
                        className="border-violet-200 text-violet-700 hover:bg-violet-50"
                        data-testid="edit-curriculum-btn"
                      >
                        <Layers className="h-4 w-4 mr-1" />
                        Curriculum
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <Tabs defaultValue="curriculum">
                    <TabsList className="mb-4">
                      <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
                      <TabsTrigger value="materials">Materials</TabsTrigger>
                      <TabsTrigger value="assignments">Assignments</TabsTrigger>
                    </TabsList>

                    {/* Curriculum Tab - View Only */}
                    <TabsContent value="curriculum">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium">Course Curriculum</h4>
                          <Button 
                            size="sm" 
                            className="bg-violet-700 hover:bg-violet-800"
                            onClick={openCurriculumEditor}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit Curriculum
                          </Button>
                        </div>

                        {(!selectedCourse.curriculum || selectedCourse.curriculum.length === 0) ? (
                          <div className="text-center py-8 bg-stone-50 rounded-xl">
                            <Layers className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                            <p className="text-stone-500 mb-4">No curriculum defined yet</p>
                            <Button variant="outline" onClick={openCurriculumEditor}>
                              <Plus className="h-4 w-4 mr-1" />
                              Add Curriculum
                            </Button>
                          </div>
                        ) : (
                          <Accordion type="single" collapsible className="w-full">
                            {selectedCourse.curriculum.map((module, idx) => (
                              <AccordionItem key={idx} value={`module-${idx}`} className="border rounded-lg mb-2 px-4">
                                <AccordionTrigger className="hover:no-underline py-4">
                                  <div className="flex items-center gap-3 text-left">
                                    <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                                      <span className="font-bold text-violet-700 text-sm">{idx + 1}</span>
                                    </div>
                                    <div>
                                      <h4 className="font-medium text-stone-900">{module.module_title}</h4>
                                      <p className="text-xs text-stone-500">{module.lessons?.length || 0} lessons</p>
                                    </div>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="pb-4">
                                  {module.module_description && (
                                    <p className="text-sm text-stone-600 mb-3 pl-11">{module.module_description}</p>
                                  )}
                                  <div className="space-y-2 pl-11">
                                    {module.lessons?.map((lesson, lessonIdx) => (
                                      <div 
                                        key={lessonIdx}
                                        className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                                      >
                                        <div className="flex items-center gap-3">
                                          {getLessonIcon(lesson.type)}
                                          <span className="text-stone-700">{lesson.title}</span>
                                        </div>
                                        <span className="text-sm text-stone-500">{lesson.duration}</span>
                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="materials">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium">Course Materials</h4>
                          <Dialog open={isMaterialOpen} onOpenChange={setIsMaterialOpen}>
                            <DialogTrigger asChild>
                              <Button size="sm" className="bg-violet-700 hover:bg-violet-800" data-testid="add-material-btn">
                                <Plus className="h-4 w-4 mr-1" />
                                Add Material
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-lg">
                              <DialogHeader>
                                <DialogTitle style={{ fontFamily: 'Outfit' }}>Add Material</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 pt-4">
                                <div>
                                  <Label>Title *</Label>
                                  <Input
                                    value={materialForm.title}
                                    onChange={(e) => setMaterialForm({ ...materialForm, title: e.target.value })}
                                    placeholder="Material title"
                                    data-testid="material-title-input"
                                  />
                                </div>
                                <div>
                                  <Label>Type</Label>
                                  <Select 
                                    value={materialForm.type} 
                                    onValueChange={(v) => setMaterialForm({ ...materialForm, type: v })}
                                  >
                                    <SelectTrigger data-testid="material-type-select">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="video">Video</SelectItem>
                                      <SelectItem value="pdf">PDF</SelectItem>
                                      <SelectItem value="document">Document</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                {/* File Upload */}
                                <div>
                                  <Label>Upload File (up to 500MB)</Label>
                                  <div className="mt-1">
                                    {materialFile ? (
                                      <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg border border-violet-200">
                                        <div className="flex items-center gap-3">
                                          <File className="h-8 w-8 text-violet-600" />
                                          <div>
                                            <p className="font-medium text-stone-900 text-sm">{materialFile.name}</p>
                                            <p className="text-xs text-stone-500">{formatFileSize(materialFile.size)}</p>
                                          </div>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={removeMaterialFile}
                                          className="text-stone-500 hover:text-red-500"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-stone-200 rounded-lg cursor-pointer hover:border-violet-400 hover:bg-violet-50 transition-colors">
                                        <FileUp className="h-8 w-8 text-stone-400 mb-2" />
                                        <span className="text-sm text-stone-500">Click to upload file</span>
                                        <span className="text-xs text-stone-400">PDF, Video, Documents up to 500MB</span>
                                        <input
                                          ref={materialFileRef}
                                          type="file"
                                          className="hidden"
                                          onChange={handleMaterialFileSelect}
                                          accept=".pdf,.doc,.docx,.mp4,.mov,.avi,.txt,.ppt,.pptx,.xls,.xlsx"
                                          data-testid="material-file-input"
                                        />
                                      </label>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="text-center text-sm text-stone-400">— OR —</div>
                                
                                <div>
                                  <Label>External URL</Label>
                                  <Input
                                    value={materialForm.url}
                                    onChange={(e) => setMaterialForm({ ...materialForm, url: e.target.value })}
                                    placeholder="https://youtube.com/..."
                                    data-testid="material-url-input"
                                  />
                                </div>
                                <div>
                                  <Label>Description</Label>
                                  <Textarea
                                    value={materialForm.description}
                                    onChange={(e) => setMaterialForm({ ...materialForm, description: e.target.value })}
                                    placeholder="Description"
                                    rows={2}
                                    data-testid="material-description-input"
                                  />
                                </div>
                                
                                {/* Upload Progress */}
                                {uploadingMaterial && (
                                  <div>
                                    <div className="flex justify-between text-sm text-stone-500 mb-1">
                                      <span>Uploading...</span>
                                      <span>{uploadProgress}%</span>
                                    </div>
                                    <Progress value={uploadProgress} className="h-2" />
                                  </div>
                                )}
                                
                                <Button 
                                  className="w-full bg-violet-700 hover:bg-violet-800"
                                  onClick={handleAddMaterial}
                                  disabled={uploadingMaterial}
                                  data-testid="submit-material-btn"
                                >
                                  {uploadingMaterial ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="h-4 w-4 mr-2" />
                                      Add Material
                                    </>
                                  )}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>

                        {selectedCourse.materials?.length === 0 ? (
                          <p className="text-stone-500 text-center py-8">No materials added yet</p>
                        ) : (
                          <div className="space-y-2">
                            {selectedCourse.materials?.map((material) => (
                              <div 
                                key={material.material_id}
                                className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg"
                              >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  material.type === 'video' ? 'bg-violet-100' : 'bg-orange-100'
                                }`}>
                                  {material.type === 'video' ? (
                                    <PlayCircle className="h-5 w-5 text-violet-700" />
                                  ) : (
                                    <FileText className="h-5 w-5 text-orange-600" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-stone-900 truncate">{material.title}</p>
                                  <div className="flex items-center gap-2">
                                    <p className="text-xs text-stone-500 capitalize">{material.type}</p>
                                    {material.file_size && (
                                      <Badge variant="secondary" className="text-xs">
                                        {formatFileSize(material.file_size)}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                {material.file_path ? (
                                  <a href={`${API}/materials/${material.material_id}/download`} target="_blank" rel="noopener noreferrer">
                                    <Button variant="ghost" size="icon" className="text-violet-700">
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </a>
                                ) : material.url && (
                                  <a href={material.url} target="_blank" rel="noopener noreferrer">
                                    <Button variant="ghost" size="icon">
                                      <LinkIcon className="h-4 w-4" />
                                    </Button>
                                  </a>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500"
                                  onClick={() => handleDeleteMaterial(material.material_id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="assignments">
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <h4 className="font-medium">Assignments</h4>
                          <Dialog open={isAssignmentOpen} onOpenChange={setIsAssignmentOpen}>
                            <DialogTrigger asChild>
                              <Button size="sm" className="bg-violet-700 hover:bg-violet-800" data-testid="add-assignment-btn">
                                <Plus className="h-4 w-4 mr-1" />
                                Add Assignment
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Assignment</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 pt-4">
                                <div>
                                  <Label>Title *</Label>
                                  <Input
                                    value={assignmentForm.title}
                                    onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })}
                                    placeholder="Assignment title"
                                    data-testid="assignment-title-input"
                                  />
                                </div>
                                <div>
                                  <Label>Description</Label>
                                  <Textarea
                                    value={assignmentForm.description}
                                    onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })}
                                    placeholder="Assignment description"
                                    rows={3}
                                    data-testid="assignment-description-input"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label>Due Date *</Label>
                                    <Input
                                      type="datetime-local"
                                      value={assignmentForm.due_date}
                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, due_date: e.target.value })}
                                      data-testid="assignment-duedate-input"
                                    />
                                  </div>
                                  <div>
                                    <Label>Total Marks</Label>
                                    <Input
                                      type="number"
                                      value={assignmentForm.total_marks}
                                      onChange={(e) => setAssignmentForm({ ...assignmentForm, total_marks: parseInt(e.target.value) })}
                                      data-testid="assignment-marks-input"
                                    />
                                  </div>
                                </div>
                                <Button 
                                  className="w-full bg-violet-700 hover:bg-violet-800"
                                  onClick={handleAddAssignment}
                                  data-testid="submit-assignment-btn"
                                >
                                  Create Assignment
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>

                        {selectedCourse.assignments?.length === 0 ? (
                          <p className="text-stone-500 text-center py-8">No assignments created yet</p>
                        ) : (
                          <div className="space-y-2">
                            {selectedCourse.assignments?.map((assignment) => (
                              <div 
                                key={assignment.assignment_id}
                                className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg"
                              >
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                  <Calendar className="h-5 w-5 text-orange-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-stone-900 truncate">{assignment.title}</p>
                                  <p className="text-xs text-stone-500">
                                    Due: {new Date(assignment.due_date).toLocaleDateString()}
                                  </p>
                                </div>
                                <Badge variant="secondary">{assignment.total_marks} marks</Badge>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500"
                                  onClick={() => handleDeleteAssignment(assignment.assignment_id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardContent className="p-12 text-center">
                  <BookOpen className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-stone-900 mb-2">Select a Course</h3>
                  <p className="text-stone-500">Click on a course to manage its content</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Edit Course Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Edit Course</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Course title"
                />
              </div>
              <div>
                <Label>Short Description</Label>
                <Input
                  value={editForm.short_description}
                  onChange={(e) => setEditForm({ ...editForm, short_description: e.target.value })}
                  placeholder="Brief course summary"
                />
              </div>
              <div>
                <Label>Full Description *</Label>
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Detailed course description"
                  rows={3}
                />
              </div>
              <div>
                <Label>Instructor *</Label>
                <Input
                  value={editForm.instructor}
                  onChange={(e) => setEditForm({ ...editForm, instructor: e.target.value })}
                  placeholder="Instructor name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Input
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    placeholder="e.g., Programming"
                  />
                </div>
                <div>
                  <Label>Duration</Label>
                  <Input
                    value={editForm.duration}
                    onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                    placeholder="e.g., 10 hours"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (GBP) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Skill Level</Label>
                  <Select 
                    value={editForm.skill_level} 
                    onValueChange={(v) => setEditForm({ ...editForm, skill_level: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Thumbnail URL</Label>
                <Input
                  value={editForm.thumbnail}
                  onChange={(e) => setEditForm({ ...editForm, thumbnail: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <Button 
                className="w-full bg-violet-700 hover:bg-violet-800"
                onClick={handleUpdateCourse}
              >
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Curriculum Editor Dialog */}
        <Dialog open={isCurriculumOpen} onOpenChange={setIsCurriculumOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }} className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-violet-700" />
                Edit Curriculum
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              {curriculumModules.length === 0 ? (
                <div className="text-center py-8 bg-stone-50 rounded-xl">
                  <Layers className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500 mb-4">No modules yet. Start building your curriculum!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {curriculumModules.map((module, moduleIdx) => (
                    <Card key={moduleIdx} className="border border-stone-200">
                      <CardContent className="p-4">
                        {/* Module Header */}
                        <div className="flex items-start gap-3 mb-4">
                          <div className="flex flex-col gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => moveModule(moduleIdx, 'up')}
                              disabled={moduleIdx === 0}
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6"
                              onClick={() => moveModule(moduleIdx, 'down')}
                              disabled={moduleIdx === curriculumModules.length - 1}
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-violet-100 text-violet-700">Module {moduleIdx + 1}</Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-500 ml-auto"
                                onClick={() => deleteModule(moduleIdx)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <Input
                              value={module.module_title}
                              onChange={(e) => updateModule(moduleIdx, 'module_title', e.target.value)}
                              placeholder="Module title"
                              className="font-semibold"
                              data-testid={`module-title-${moduleIdx}`}
                            />
                            <Textarea
                              value={module.module_description || ''}
                              onChange={(e) => updateModule(moduleIdx, 'module_description', e.target.value)}
                              placeholder="Module description (optional)"
                              rows={2}
                              className="text-sm"
                            />
                          </div>
                        </div>

                        {/* Lessons */}
                        <div className="pl-10 space-y-2">
                          <p className="text-sm font-medium text-stone-700 mb-2">
                            Lessons <span className="text-xs text-stone-400">(drag to reorder)</span>
                          </p>
                          {(module.lessons || []).map((lesson, lessonIdx) => (
                            <div 
                              key={lessonIdx} 
                              className={`flex items-center gap-2 p-2 bg-stone-50 rounded-lg cursor-move ${
                                draggedLesson?.moduleIndex === moduleIdx && draggedLesson?.lessonIndex === lessonIdx
                                  ? 'ring-2 ring-violet-500 bg-violet-50'
                                  : ''
                              }`}
                              draggable
                              onDragStart={() => handleLessonDragStart(moduleIdx, lessonIdx)}
                              onDragOver={(e) => handleLessonDragOver(e, moduleIdx, lessonIdx)}
                              onDragEnd={handleLessonDragEnd}
                            >
                              <GripVertical className="h-4 w-4 text-stone-400 shrink-0" />
                              <div className="flex flex-col shrink-0">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  onClick={() => moveLessonInModule(moduleIdx, lessonIdx, 'up')}
                                  disabled={lessonIdx === 0}
                                >
                                  <ChevronUp className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  onClick={() => moveLessonInModule(moduleIdx, lessonIdx, 'down')}
                                  disabled={lessonIdx === (module.lessons?.length || 0) - 1}
                                >
                                  <ChevronDown className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="flex-1 grid grid-cols-3 gap-2">
                                <Input
                                  value={lesson.title}
                                  onChange={(e) => updateLesson(moduleIdx, lessonIdx, 'title', e.target.value)}
                                  placeholder="Lesson title"
                                  className="col-span-1 text-sm h-8"
                                  data-testid={`lesson-title-${moduleIdx}-${lessonIdx}`}
                                />
                                <Select 
                                  value={lesson.type} 
                                  onValueChange={(v) => updateLesson(moduleIdx, lessonIdx, 'type', v)}
                                >
                                  <SelectTrigger className="h-8 text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="video">Video</SelectItem>
                                    <SelectItem value="document">Document</SelectItem>
                                    <SelectItem value="assignment">Assignment</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  value={lesson.duration || ''}
                                  onChange={(e) => updateLesson(moduleIdx, lessonIdx, 'duration', e.target.value)}
                                  placeholder="Duration"
                                  className="text-sm h-8"
                                />
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500"
                                onClick={() => deleteLesson(moduleIdx, lessonIdx)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => addLesson(moduleIdx)}
                            className="w-full mt-2"
                            data-testid={`add-lesson-btn-${moduleIdx}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Lesson
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <Button
                variant="outline"
                onClick={addModule}
                className="w-full"
                data-testid="add-module-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Module
              </Button>

              {/* Import Curriculum Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-stone-700">Import from CSV/JSON</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={downloadCurriculumTemplate}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download Template
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="file"
                    accept=".csv,.json"
                    ref={curriculumFileRef}
                    onChange={(e) => setCurriculumImportFile(e.target.files[0])}
                    className="flex-1"
                    data-testid="curriculum-import-file"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleImportCurriculum(false)}
                    disabled={!curriculumImportFile || importingCurriculum}
                    data-testid="import-append-btn"
                  >
                    {importingCurriculum ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <FileUp className="h-4 w-4 mr-1" />
                        Append
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleImportCurriculum(true)}
                    disabled={!curriculumImportFile || importingCurriculum}
                    className="text-orange-600 border-orange-200 hover:bg-orange-50"
                    data-testid="import-replace-btn"
                  >
                    Replace All
                  </Button>
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  CSV columns: module_title, lesson_title, lesson_duration, lesson_type
                </p>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setIsCurriculumOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-violet-700 hover:bg-violet-800"
                onClick={saveCurriculum}
                data-testid="save-curriculum-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Curriculum
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminCourses;
